import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import {
    PrescripteursSelector,
    selectedPrescripteursSelector,
    SousClient,
    SousClientApi,
    useTiersService,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import { ChoiceItemProps, ChoiceList } from '@europrocurement/flexy-components';
import { AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { useDebouncedCallback } from 'use-debounce';
import { AppDispatch } from '@europrocurement/flexy-components/redux/storeConfig/store';

export type SelectPrescriberProps = {
    onSelect?: (ids: number[], prescripteurs: SousClientApi[]) => void;
    multiple?: boolean;
    title?: string;
    description?: string;
};

const addressToString = (address: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup) => {
    let addresseFact = '';
    if (address.adresseApi !== null && address.adresseApi !== '') {
        addresseFact = address.adresseApi ? address.adresseApi : '';
    } else {
        addresseFact = [
            address.adresse1,
            address.adresse2,
            address.adresse3,
            address.adresse4,
            address.codePostal,
            address.ville,
        ]
            .filter(Boolean)
            .join(' ');
    }
    return addresseFact;
};
const selectAddressFacturante = (
    addresses: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup[],
) => addresses.filter((adresse) => adresse.facturation === true);

const SelectPrescriber: React.FunctionComponent<SelectPrescriberProps> = function ({
    onSelect,
    multiple = false,
    title = 'Pour quel prescripteur est destinée la commande ?',
    description = 'Sélectionner le prescripteur',
}) {
    const datasource = useTiersServiceSelector(PrescripteursSelector).main;
    const prescriber = useTiersServiceSelector(selectedPrescripteursSelector);
    const [prescribers, setPrescribers] = useState<Array<SousClient>>([]);
    const [listFiltered, setListFiltered] = useState<Array<ChoiceItemProps>>([]); // liste filtré par la fonction search
    const [prescriberSelected, setPrescriberSelected] = useState<Array<string>>([]); // tableau d'id sélectionné
    const { getPrescripteurs } = useTiersService();
    const dispatch = useDispatch<AppDispatch>();

    if (!prescriber || prescriber.id === undefined) {
        throw new Error('no prescriber');
    }

    // suppression de la recherche à l'entrée du formulaire et sortie de la page
    useEffect(() => {
        dispatch({
            type: 'prescripteur/resetmainSearch',
        });

        return () => {
            dispatch({
                type: 'prescripteur/resetmainSearch',
            });
            dispatch({
                type: 'prescripteur/resetmainFilter',
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (datasource.status === 'succeeded') {
            setPrescribers(datasource.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasource.status]);

    // MAJ sousclient en fonction du datasource etc...
    useEffect(() => {
        if (datasource.status === 'succeeded') {
            const listSC: ChoiceItemProps[] = [];
            prescribers.forEach((sclient) => {
                if (sclient.id === undefined) {
                    return;
                }
                const obj: ChoiceItemProps = {
                    label: sclient.raisonSociale,
                    value: sclient.id.toString(),
                };

                // Récupère le siren en priorité ou le tvaIntracommunautaire
                if (sclient.siren && sclient.siren.trim() !== '') {
                    Object.assign(obj, { idFiscal: `Siren : ${sclient.siren}` });
                } else if (
                    sclient.tvaIntracommunautaire &&
                    sclient.tvaIntracommunautaire.trim() !== ''
                ) {
                    Object.assign(obj, {
                        idFiscal: `Numero d’identification fiscal : ${sclient.tvaIntracommunautaire}`,
                    });
                }

                if (sclient.siren !== '') {
                    Object.assign(obj, { siren: sclient.siren });
                }
                const address = selectAddressFacturante(sclient.adresses);
                if (address.length > 0) {
                    Object.assign(obj, { description: addressToString(address[0]) });
                }

                listSC.push(obj);
            });
            setListFiltered(listSC);
        }
    }, [datasource, prescribers]);

    const onSubmitWrapper = useCallback(
        (data: { idsSociete: Array<string> }) => {
            const arrayIds = data.idsSociete;

            if (onSelect) {
                onSelect(
                    arrayIds.map((str: string) => Number(str)),
                    [...prescribers, prescriber].filter(
                        (prescripteur) => arrayIds.indexOf(`${prescripteur.id}`) !== -1,
                    ),
                );
            }
        },
        [onSelect, prescribers, prescriber],
    );

    // APPEL OPENSEARCH au changement du filtre
    const onSearchWrapper = useDebouncedCallback((currentSearchString: string) => {
        if (currentSearchString !== '') {
            dispatch({
                type: 'prescripteur/setmainSearch',
                payload: {
                    search: currentSearchString,
                },
            });
            dispatch(getPrescripteurs({}) as unknown as AnyAction);
        } else {
            dispatch({
                type: 'prescripteur/resetmainSearch',
            });
            dispatch(getPrescripteurs({}) as unknown as AnyAction);
        }
    }, 500);

    const onChangeWrapper = (arraySelected: string[]) => {
        setPrescriberSelected(arraySelected);
    };

    return (
        <ChoiceList
            title={title}
            description={description}
            submitButtonLabel="Continuer"
            placeholderLabelSearch="Nom du prescripteur concerné"
            listChoice={[
                {
                    label: 'Prescripteurs',
                    listChoice: listFiltered,
                    multiple,
                    topSelected: true,
                },
            ]}
            multiple={multiple}
            onSubmit={onSubmitWrapper}
            search={onSearchWrapper}
            onChange={onChangeWrapper}
            defaultValues={prescriberSelected}
        />
    );
};

export default SelectPrescriber;
